import { createFileRoute } from "@tanstack/react-router";
import type { FBXUserPropertiesMaterialChoice } from "building-types/materials/materialTypes";
import { loadPropLibrary } from "building-types/props/propLibraryRuntime";
import { GLTF_ASSET_SCALE } from "common/constants/assetConstants";
import { DM_Glass_Base } from "common/materials/library/designMaterialPath";
import type { MaterialSrc } from "common/materials/materialSrc";
import { isValidDesignMaterialSrc } from "common/materials/materialSrc";
import { assertIsNotNull, assertIsNotUndefined } from "common/utils/assertOfType";
import stringApplySearch from "common/utils/stringApplySearch";
import { aabb2New } from "geom/aabb2Types";
import { aabb2Width } from "geom/aabb2Types";
import { aabb2Height } from "geom/aabb2Types";
import type { AABB2 } from "geom/aabb2Types";
import { planeNewXY } from "geom/plane";
import { vec3ClampLength } from "geom/vec3";
import { dynamicOrthographicCameraSettings, type CameraSettings } from "gfx/CameraSettings";
import * as convert from "gfx/convert";
import { debugPlaneToTHREE } from "gfx/convert";
import { EnvPreset } from "gfx/EnvPreset";
import { HighlightDataTexture } from "gfx/HighlightDataTexture";
import { debugPageTransparentGroundPlaneMaterial } from "gfx/materials/debugPageTransparentGroundPlaneMaterial";
import { designMaterialNew } from "gfx/materials/designMaterial";
import { cloneDefaultMaterialParams } from "gfx/materials/designMaterialUtils";
import { GridShader } from "gfx/materials/shaders/gridShader";
import type { MeshDataStats } from "gfx/MeshDataStats";
import {
  dataStatsFromThreeMesh,
  dataStatsFromThreeObjectHierarchy,
  formatBytes,
  meshDataStatsNew,
  meshDataStatsSum,
} from "gfx/MeshDataStats";
import OrthographicControls from "gfx/OrthographicControls";
import PerspectiveControls from "gfx/PerspectiveControls";
import { DESIGN_VIEW_PREFIX, propGltfFileNameFromName } from "gfx/propFilenames";
import { envPresetSceneLights } from "gfx/sceneLights";
import THREEAssetLoader from "gfx/THREEAssetLoader";
import { visualizeDuplicateTriangles } from "gfx/threejs/higharc/countDuplicateTriangles";
import { NDCBoundingBox } from "gfx/threejs/higharc/NDCBoundingBox";
import { SSFXRendererOutput } from "gfx/threejs/higharc/SSFXRendererOutput";
import { visualizeEdgesWithStretchedUVs } from "gfx/threejs/higharc/stretchedUVsDebug";
import { visualizeHolesInGeometry } from "gfx/threejs/higharc/validateHolesInGeometry";
import type THREEViewer from "gfx/THREEViewer";
import type { THREEViewerBase } from "gfx/THREEViewerBase";
import type { THREEViewerSystem } from "gfx/THREEViewerSystem";
import { addPropGeometryToGroup } from "lib/components/studio/editorViewer/addPropGeometryToGroup";
import useTHREEViewerLite from "lib/components/studio/editorViewer/useTHREEViewerLite";
import useTHREEViewerSystem from "lib/components/studio/useTHREEViewerSystem";
import getPageTitle from "lib/utils/getPageTitle";
import type { ReactNode } from "react";
import React, { useEffect, useRef, useState } from "react";
import { Helmet as Head } from "react-helmet-async";
import * as THREE from "three";
import { Box3, Euler, Mesh, Vector3 } from "three";
import Button from "ui/buttons/Button";
import Checkbox from "ui/forms/CheckboxOld";
import withAuthentication from "../../components/withAuthentication";

const MIN_CAMERA_DISTANCE_ON_SELECT = 80;
const MAX_CAMERA_DISTANCE = 10000;

const DUPLICATE_TRI_COLOR = "#f02299";
const HOLE_COLOR = "#7711aa";
const NON_MANIFOLD_COLOR = "#aa88ff";
const STRETCHED_UV_COLOR = "#00ffff";
const STRETCHED_UV_COLOR2 = "#00b6ff";
const STRETCHED_UV_COLOR3 = "#ff0000";
const STRETCHED_UV_COLOR4 = "#ffff00";

interface PropsState {
  selectedTriangles: number;
  selectedVertices: number;
  selectedMemory: number;
  selectedSubmeshTriangles: number;
  selectedSubmeshVertices: number;
  selectedProp: string;
  selectedBoundingBox: string;
}

interface CategoryStats {
  numberOfProps: number;
  totalMemory: number;
  totalVertices: number;
  totalTriangles: number;
}

const stretchedUvMaterial = new THREE.LineBasicMaterial({ vertexColors: true, depthTest: false, transparent: true });
const duplicateTriMaterial = new THREE.LineBasicMaterial({
  color: new THREE.Color(DUPLICATE_TRI_COLOR),
  depthTest: false,
  transparent: true,
});
const holeMaterial = new THREE.LineBasicMaterial({
  color: new THREE.Color(HOLE_COLOR),
  depthTest: false,
  transparent: true,
});
const nonManifoldMaterial = new THREE.LineBasicMaterial({
  color: new THREE.Color(NON_MANIFOLD_COLOR),
  depthTest: false,
  transparent: true,
});

function PaddedLabel({ children }: { children: ReactNode }) {
  return <label style={{ padding: 10 }}>{children}</label>;
}

function trimDisplayedName(displayedName: string) {
  if (displayedName.endsWith(".glb")) {
    displayedName = displayedName.substring(0, displayedName.length - 4);
  }
  if (displayedName.startsWith(DESIGN_VIEW_PREFIX)) {
    displayedName = displayedName.substring(DESIGN_VIEW_PREFIX.length);
  }
  return displayedName;
}

// Normally the prop category is the first part of the prop asset name, but there are some special cases where we split props into more fine-grained categories.
const specialCategoryNames: readonly string[] = ["DoorGar_D", "DoorGar_S"];

function splitPropCategoryFromName(name: string) {
  for (const specialCategoryName of specialCategoryNames) {
    if (name.startsWith(specialCategoryName)) {
      return specialCategoryName;
    }
  }
  const underscoreIndex = name.indexOf("_");
  return underscoreIndex === -1 ? name : name.substring(0, underscoreIndex);
}

// Similar to propSetMaterial in materialMapping.ts, but we use the color loaded from the prop model file instead of using a design material palette in case the prop specifies a palette material.
function setMaterialsOnProp(object: THREE.Object3D, viewerSystem: THREEViewerSystem) {
  object.traverse(async (obj) => {
    if (obj instanceof Mesh && obj.material instanceof THREE.Material) {
      // The "higharcMaterial" value set on loaded geometry may specify:
      // 1) a palette id "exteriorWall0"
      // 2) a DesignMaterial: "Paint_Block_0"
      const materialId: FBXUserPropertiesMaterialChoice =
        obj.material.userData.fromFBX?.userProperties?.higharcMaterial?.value;

      if (isValidDesignMaterialSrc(materialId as MaterialSrc)) {
        obj.material = await viewerSystem.materialFactory.getDesignMaterial(materialId as MaterialSrc);
        return;
      }

      // if the material on the prop is undefined or there is a palette material id, then we use the default material.
      if (typeof materialId === "string" && materialId.startsWith("glass")) {
        obj.material = await viewerSystem.materialFactory.getDesignMaterial(DM_Glass_Base.Default);
      } else {
        const params = cloneDefaultMaterialParams();
        const viewerRenderer = viewerSystem.viewer.viewerRenderer;
        params.color = "#aaaaaa";
        if (obj.material instanceof THREE.MeshStandardMaterial || obj.material instanceof THREE.MeshBasicMaterial) {
          params.color = "#" + obj.material.color.getHexString();
        }
        obj.material = await designMaterialNew(
          params,
          viewerRenderer.renderer,
          (_url, _overrideFormat) => [null, new Promise((resolve, _reject) => resolve())],
          viewerSystem.viewer.hasSSFXRenderer,
          undefined,
        );
      }
    }
  });
}

function updateSvgViewerCamera(centeredBb: AABB2, svgViewer: THREEViewerBase) {
  const w = aabb2Width(centeredBb);
  const h = aabb2Height(centeredBb);
  svgViewer.setCameraSettings(dynamicOrthographicCameraSettings(0, 0, 0, 0, 0, -1, 0, 1, 0, w * 2.2, h * 2.2));

  const controls = new OrthographicControls(svgViewer.camera, svgViewer.container, 0, 0, 0);
  controls.minZoom = 0.1;
  controls.maxZoom = 100;
  // Since the view is always top-down here, we don't need to set accurate z values for the pan container.
  controls.panConstraintPadding = 0;
  controls.panContainBox = {
    ...centeredBb,
    minZ: 0,
    maxZ: 100,
  };

  svgViewer.setControls(controls);
}

async function loadPropFromGLTF(assetLoader: THREEAssetLoader, url: string, viewerSystem: THREEViewerSystem) {
  const prop = await convert.gltfToTHREE(assetLoader, url);
  setMaterialsOnProp(prop, viewerSystem);
  return prop;
}

function useDebugVisParent(debugVisEnabled: boolean, viewerSystem: React.RefObject<THREEViewerSystem | null>) {
  const debugVisParent = useRef<THREE.Object3D>(new THREE.Object3D());
  useEffect(() => {
    if (debugVisParent.current && viewerSystem.current) {
      const scene = viewerSystem.current.viewer.scene;
      if (debugVisEnabled) {
        scene.add(debugVisParent.current);
      } else {
        scene.remove(debugVisParent.current);
      }
    }
  }, [debugVisEnabled]);
  return debugVisParent;
}

function ColorPreview({ color }: { color: string }) {
  return <div style={{ display: "inline-block", width: 10, height: 10, backgroundColor: color, borderRadius: 3 }} />;
}

function DebugPropsPage() {
  const [state, setState] = useState<PropsState>(() => ({
    selectedTriangles: 0,
    selectedVertices: 0,
    selectedMemory: 0,
    selectedSubmeshTriangles: 0,
    selectedSubmeshVertices: 0,
    selectedProp: "",
    selectedBoundingBox: "",
  }));

  const assetLoader = THREEAssetLoader.instance;

  const viewerContainer = useRef<HTMLDivElement | null>(null);

  const viewerSystem = useRef<THREEViewerSystem | null>(null);
  const { renderID } = useTHREEViewerSystem({
    viewerSystem,
    viewerContainerRef: viewerContainer,
    setLoading: () => {},
  });

  const [filterString, setFilterString] = useState("");
  const [categories, setCategories] = useState<{
    [key: string]: string[];
  }>({});
  const [canvasCursor, setCanvasCursor] = useState<string>("default");
  const [selectedCategory, setSelectedCategory] = useState<string>();
  const [categoryStats, setCategoryStats] = useState<CategoryStats>({
    numberOfProps: 0,
    totalMemory: 0,
    totalTriangles: 0,
    totalVertices: 0,
  });
  const [uvVisualizationEnabled, setUvVisualizationEnabled] = useState<boolean>(false);
  const [uvScalingTolerance, setUvScalingTolerance] = useState<number>(0.5);
  const gridMeshRef = useRef<THREE.Mesh | null>(null);
  const [selectedMesh, setSelectedMesh] = useState<{
    rootMesh: THREE.Object3D;
    mesh: THREE.Object3D;
  } | null>(null);

  const categoryStatsMap = useRef<{
    [categoryName: string]: CategoryStats;
  }>({});
  const loadedProps = useRef<{
    [key: string]: THREE.Object3D;
  }>({});

  const [duplicateTrisDebugVisEnabled, setDuplicateTrisDebugVisEnabled] = useState<boolean>(true);
  const [uvDebugVisEnabled, setUvDebugVisEnabled] = useState<boolean>(true);
  const [holeDebugVisEnabled, setHoleDebugVisEnabled] = useState<boolean>(false);
  const duplicateTrisDebugVisParent = useDebugVisParent(duplicateTrisDebugVisEnabled, viewerSystem);
  const uvDebugVisParent = useDebugVisParent(uvDebugVisEnabled, viewerSystem);
  const holeDebugVisParent = useDebugVisParent(holeDebugVisEnabled, viewerSystem);
  const meshesDebugVis = useRef<{
    [name: string]: {
      uvDebugVis: THREE.Object3D;
      holeDebugVis: THREE.Object3D;
      duplicateTriDebugVis: THREE.Object3D;
      uvScalingTolerance: number;
    };
  }>({});
  const svgViewerContainer = useRef<HTMLDivElement | null>(null);
  const svgViewer = useRef<THREEViewerBase | null>(null);
  const svgPreviewGroupRef = useRef<THREE.Group | null>(null);
  const highlightDataTexture = useRef<HighlightDataTexture>(new HighlightDataTexture());
  useTHREEViewerLite({
    viewerRef: svgViewer,
    viewerContainerRef: svgViewerContainer,
    viewName: "svg",
    options: { highlightDataTexture: highlightDataTexture.current },
  });

  useEffect(() => {
    assertIsNotNull(svgViewer.current);
    const svgPreviewGroup = new THREE.Group();
    svgViewer.current.add(svgPreviewGroup);
    svgPreviewGroupRef.current = svgPreviewGroup;

    svgViewer.current.add(debugPlaneToTHREE(planeNewXY()));
  }, []);

  const setCamera = (viewer: THREEViewer, cameraSettings: CameraSettings) => {
    viewer.setCameraSettings(cameraSettings);
    const controls = new PerspectiveControls(
      viewer.camera,
      viewer.container,
      cameraSettings.targetX,
      cameraSettings.targetY,
      cameraSettings.targetZ,
    );
    controls.maxPolarAngle = Math.PI / 2;
    controls.maxDistance = MAX_CAMERA_DISTANCE;
    viewer.setControls(controls);
  };

  const selectMesh = (mesh: THREE.Object3D) => {
    let rootMesh: THREE.Object3D = mesh;
    assertIsNotNull(viewerSystem.current);
    while (rootMesh.parent && rootMesh.parent !== viewerSystem.current.viewer.scene.scene) {
      rootMesh = rootMesh.parent;
    }

    const propDataStats = dataStatsFromThreeObjectHierarchy(rootMesh);
    let submeshDataStats: MeshDataStats = meshDataStatsNew();

    if (mesh instanceof THREE.Mesh && mesh.geometry) {
      submeshDataStats = dataStatsFromThreeMesh(mesh);
    }

    const bb = new Box3().setFromObject(mesh);

    rootMesh.traverse((node) => {
      if (node instanceof THREE.Mesh) {
        bb.expandByObject(node);
      }
    });

    const bbCenter = new THREE.Vector3();
    bb.getCenter(bbCenter);
    const bbSize = new THREE.Vector3();
    bb.getSize(bbSize);

    const normalizedBb = aabb2New(
      bb.min.x - rootMesh.position.x,
      bb.min.y - rootMesh.position.y,
      bb.max.x - rootMesh.position.x,
      bb.max.y - rootMesh.position.y,
    );

    const maxDimensionSize = Math.max(bbSize.x, bbSize.y, bbSize.z);

    const cameraOffset = new THREE.Vector3(-maxDimensionSize, -maxDimensionSize, maxDimensionSize);
    cameraOffset.multiplyScalar(1.5);

    const cameraSettings = {
      name: "none",
      fov: 50,
      ortho: false,
      x: bbCenter.x + cameraOffset.x,
      y: bbCenter.y + cameraOffset.y,
      z: bbCenter.z + cameraOffset.z,
      targetX: bbCenter.x,
      targetY: bbCenter.y,
      targetZ: bbCenter.z,
      upX: 0,
      upY: 0,
      upZ: 1,
      cameraFar: 20000,
    };

    const viewer = viewerSystem.current.viewer;

    const camera = convert.cameraToTHREE(
      cameraSettings,
      viewer.container ? viewer.container.clientWidth / viewer.container.clientHeight : 1,
    );
    const propNdcBB = NDCBoundingBox(rootMesh, camera);

    const maxNdcBBValue = Math.max(-propNdcBB.minX, -propNdcBB.minY, propNdcBB.maxX, propNdcBB.maxY);
    cameraOffset.multiplyScalar(maxNdcBBValue * 1.5);
    cameraOffset.clampLength(MIN_CAMERA_DISTANCE_ON_SELECT, MAX_CAMERA_DISTANCE);

    cameraSettings.x = bbCenter.x + cameraOffset.x;
    cameraSettings.y = bbCenter.y + cameraOffset.y;
    cameraSettings.z = bbCenter.z + cameraOffset.z;

    setCamera(viewer, cameraSettings);

    assertIsNotNull(svgViewer.current);
    const fillMat = svgViewer.current.getMeshBasicMaterial({
      color: "lightgrey",
      transparent: true,
      opacity: 0.7,
    });

    assertIsNotNull(svgPreviewGroupRef.current);
    svgPreviewGroupRef.current.clear();
    void addPropGeometryToGroup(
      svgPreviewGroupRef.current,
      rootMesh.name,
      { integerId: 0, scaleX: 1, scaleY: 1, x: 0, y: 0, rotationDegrees: 0 },
      svgViewer.current,
      fillMat,
    ).then(() => {
      assertIsNotNull(svgViewer.current);
      updateSvgViewerCamera(normalizedBb, svgViewer.current);
    });

    setState({
      ...state,
      selectedProp: rootMesh.name,
      selectedVertices: propDataStats.numVerts,
      selectedTriangles: propDataStats.numTris,
      selectedMemory: propDataStats.memoryBytes,
      selectedBoundingBox: `${bbSize.x.toFixed(2)}×${bbSize.y.toFixed(2)}×${bbSize.z.toFixed(2)}`,
      selectedSubmeshVertices: submeshDataStats.numVerts,
      selectedSubmeshTriangles: submeshDataStats.numTris,
    });

    setSelectedMesh({ rootMesh, mesh });
  };

  useEffect(() => {
    if (!selectedMesh) {
      return;
    }
    const { rootMesh } = selectedMesh;

    if (
      meshesDebugVis.current[rootMesh.name] === undefined ||
      uvScalingTolerance !== meshesDebugVis.current[rootMesh.name].uvScalingTolerance
    ) {
      if (meshesDebugVis.current[rootMesh.name]) {
        duplicateTrisDebugVisParent.current.remove(meshesDebugVis.current[rootMesh.name].duplicateTriDebugVis);
        uvDebugVisParent.current.remove(meshesDebugVis.current[rootMesh.name].uvDebugVis);
        holeDebugVisParent.current.remove(meshesDebugVis.current[rootMesh.name].holeDebugVis);
      }
      const meshDebugVis = {
        uvDebugVis: new THREE.Object3D(),
        holeDebugVis: new THREE.Object3D(),
        duplicateTriDebugVis: new THREE.Object3D(),
        uvScalingTolerance: uvScalingTolerance,
      };
      duplicateTrisDebugVisParent.current.add(meshDebugVis.duplicateTriDebugVis);
      uvDebugVisParent.current.add(meshDebugVis.uvDebugVis);
      holeDebugVisParent.current.add(meshDebugVis.holeDebugVis);
      meshesDebugVis.current[rootMesh.name] = meshDebugVis;

      rootMesh.traverse((node) => {
        if (node instanceof THREE.Mesh) {
          const stretchedUvGeometry = visualizeEdgesWithStretchedUVs(
            node.geometry,
            GLTF_ASSET_SCALE,
            1 / (1 + uvScalingTolerance),
            1 + uvScalingTolerance,
          );
          if (stretchedUvGeometry) {
            stretchedUvGeometry.userData.isCached = true;
            const stretchedUvMesh = new THREE.LineSegments(stretchedUvGeometry, stretchedUvMaterial);
            stretchedUvMesh.renderOrder = 1;
            meshDebugVis.uvDebugVis.add(stretchedUvMesh);
            stretchedUvMesh.matrixWorld = node.matrixWorld;
            stretchedUvMesh.matrixWorldAutoUpdate = false;
          }
          const duplicateGeometry = visualizeDuplicateTriangles(node.geometry);
          if (duplicateGeometry) {
            duplicateGeometry.userData.isCached = true;
            const duplicateTriMesh = new THREE.LineSegments(duplicateGeometry, duplicateTriMaterial);
            duplicateTriMesh.renderOrder = 1;
            meshDebugVis.duplicateTriDebugVis.add(duplicateTriMesh);
            duplicateTriMesh.matrixWorld = node.matrixWorld;
            duplicateTriMesh.matrixWorldAutoUpdate = false;
          }
          const holes = visualizeHolesInGeometry(node.geometry);
          if (holes.holeLinesGeometry) {
            holes.holeLinesGeometry.userData.isCached = true;
            const holeMesh = new THREE.LineSegments(holes.holeLinesGeometry, holeMaterial);
            holeMesh.renderOrder = 1;
            meshDebugVis.holeDebugVis.add(holeMesh);
            holeMesh.matrixWorld = node.matrixWorld;
            holeMesh.matrixWorldAutoUpdate = false;
          }
          if (holes.nonManifoldLinesGeometry) {
            holes.nonManifoldLinesGeometry.userData.isCached = true;
            const nonManifoldMesh = new THREE.LineSegments(holes.nonManifoldLinesGeometry, nonManifoldMaterial);
            nonManifoldMesh.renderOrder = 1;
            meshDebugVis.holeDebugVis.add(nonManifoldMesh);
            nonManifoldMesh.matrixWorld = node.matrixWorld;
            nonManifoldMesh.matrixWorldAutoUpdate = false;
          }
        }
      });
    } else {
      duplicateTrisDebugVisParent.current.add(meshesDebugVis.current[rootMesh.name].duplicateTriDebugVis);
      uvDebugVisParent.current.add(meshesDebugVis.current[rootMesh.name].uvDebugVis);
      holeDebugVisParent.current.add(meshesDebugVis.current[rootMesh.name].holeDebugVis);
    }

    viewerSystem.current?.viewer.scene.markRenderingNeedsUpdate();
  }, [selectedMesh, uvScalingTolerance]);

  function findMeshByMousePos(e: React.MouseEvent<HTMLDivElement, MouseEvent>) {
    if (!viewerSystem.current) {
      return undefined;
    }
    const viewer = viewerSystem.current.viewer;
    const ints = viewer.raycast(e.clientX, e.clientY);
    if (ints.length === 0) {
      return undefined;
    }

    for (const int of ints) {
      if (int.object instanceof Mesh && int.object.userData.isProp) {
        return int.object;
      }
    }
    return undefined;
  }

  const mouseUp = async (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (isDrag.current) {
      isMouseDown.current = false;
      isDrag.current = false;
      return;
    }

    isMouseDown.current = false;
    isDrag.current = false;

    const mesh = findMeshByMousePos(e);

    if (mesh) {
      selectMesh(mesh);
    }
  };

  const isMouseDown = useRef(false);
  const isDrag = useRef(false);

  const mouseDown = () => {
    isMouseDown.current = true;
  };

  const mouseMove = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    let hoverCursor = "default";
    if (isMouseDown.current) {
      isDrag.current = true;
    } else {
      const hoverMesh = findMeshByMousePos(e);
      if (hoverMesh) {
        hoverCursor = "pointer";
      }
    }
    setCanvasCursor(hoverCursor);
  };

  useEffect(() => {
    if (viewerSystem.current) {
      assertIsNotUndefined(viewerSystem.current.viewer.scene.ssfxRendererScene);
      void viewerSystem.current.viewer.scene.ssfxRendererScene.sceneLightsManager.renderSceneLights(
        envPresetSceneLights[EnvPreset.WHITEBOX_LOW_FOG],
        {
          exteriorLightProbesEnabled: true,
        },
      );
      void loadPropCategories();
    }
  }, [renderID]);

  useEffect(() => {
    if (uvVisualizationEnabled) {
      viewerSystem.current?.viewer.scene.ssfxRendererScene?.updateRenderParams({
        renderPipelineParams: { output: SSFXRendererOutput.UV_CALIBRATION },
      });
    } else {
      viewerSystem.current?.viewer.scene.ssfxRendererScene?.updateRenderParams({
        renderPipelineParams: { output: SSFXRendererOutput.FINAL_RENDER_WITH_EFFECTS },
      });
    }
    if (gridMeshRef.current) {
      gridMeshRef.current.visible = !uvVisualizationEnabled;
    }
    if (viewerSystem.current) {
      viewerSystem.current.viewer.scene.groundPlaneMaterial = uvVisualizationEnabled
        ? undefined
        : debugPageTransparentGroundPlaneMaterial;
    }
  }, [renderID, uvVisualizationEnabled]);

  useEffect(() => {
    void layoutCategory();
  }, [selectedCategory]);

  useEffect(() => {
    void layoutSelection();
  }, [filterString]);

  const layoutCategory = async () => {
    if (!selectedCategory) {
      return;
    }
    const category = categories[selectedCategory];

    if (!viewerSystem.current || !category) {
      return;
    }

    const viewer = viewerSystem.current.viewer;

    await loadProps(category, selectedCategory);

    const models: {
      [name: string]: THREE.Object3D;
    } = {};
    for (const propName of category) {
      models[propName] = loadedProps.current[propName];
    }
    viewer.scene.clear();

    duplicateTrisDebugVisParent.current.clear();
    if (duplicateTrisDebugVisEnabled) {
      viewer.scene.add(duplicateTrisDebugVisParent.current);
    }
    uvDebugVisParent.current.clear();
    if (uvDebugVisEnabled) {
      viewer.scene.add(uvDebugVisParent.current);
    }
    holeDebugVisParent.current.clear();
    if (holeDebugVisEnabled) {
      viewer.scene.add(holeDebugVisParent.current);
    }

    const bbsize = new Vector3();
    const maxBb = new Vector3();

    for (const scene of Object.values(models)) {
      const boundingBox = new Box3().setFromObject(scene);
      boundingBox.getSize(bbsize);
      maxBb.max(bbsize);
    }

    // Make sure that objects don't overlap, the objects are aligned with the grid, and that tall objects with small footprint are not placed too close to each other.
    const xInterval = Math.ceil((Math.max(maxBb.x, maxBb.z) + 36) / 60) * 60;
    const yInterval = Math.ceil((Math.max(maxBb.y, maxBb.z) + 36) / 60) * 60;

    const rowCount = Math.sqrt(category.length) | 0;

    const minx = Math.floor((-xInterval * rowCount) / 2 / 60) * 60;
    const miny = Math.floor((-yInterval * rowCount) / 2 / 60) * 60;

    let px = minx;
    let py = miny;

    let count = 0;

    // Note: grid geometry size should be divisible by 60!
    const planeGeom = new THREE.PlaneGeometry(19200, 19200, 1, 1);
    const gridMaterial = new THREE.ShaderMaterial({
      defines: {},
      fragmentShader: GridShader.fragmentShader,
      vertexShader: GridShader.vertexShader,
      uniforms: THREE.UniformsUtils.clone(GridShader.uniforms),
      blending: THREE.NormalBlending,
      depthTest: true,
      depthWrite: false,
      transparent: true,
    });
    gridMaterial.uniforms.fadeAxes.value = true;
    gridMaterial.uniforms.alphaMult.value = 0.25;
    gridMaterial.uniforms.color.value.set(0, 0, 0);
    gridMaterial.uniforms.xAxisColor.value.copy(gridMaterial.uniforms.color.value);
    gridMaterial.uniforms.yAxisColor.value.copy(gridMaterial.uniforms.color.value);
    const gridMesh = new THREE.Mesh(planeGeom, gridMaterial);
    gridMesh.renderOrder = 1; // Always draw after ground
    gridMesh.position.z = -0.1;
    gridMesh.updateMatrix();
    gridMesh.updateMatrixWorld();
    viewer.scene.add(gridMesh);
    gridMesh.visible = !uvVisualizationEnabled;
    gridMeshRef.current = gridMesh;

    for (const propName in models) {
      const mesh = models[propName];

      mesh.position.x = px;
      mesh.position.y = py;

      mesh.updateMatrix();
      mesh.updateMatrixWorld();

      viewer.scene.add(
        debugPlaneToTHREE({
          ...planeNewXY(),
          originX: mesh.position.x,
          originY: mesh.position.y,
        }),
      );

      mesh.name = propName; // This name is shown on the UI when picking a prop. A different object name might have been stored inside the loaded file.
      viewer.scene.add(mesh);

      px += xInterval;

      ++count;
      if (count % rowCount === 0) {
        px = minx;
        py += yInterval;
      }
    }

    const [camPosX, camPosY, camPosZ] = vec3ClampLength(
      1.5 * minx,
      1.5 * miny,
      -1.5 * Math.min(minx, miny),
      100,
      MAX_CAMERA_DISTANCE,
    );

    setCamera(viewer, {
      name: "none",
      fov: 60,
      x: camPosX,
      y: camPosY,
      z: camPosZ,
      targetX: 0,
      targetY: 0,
      targetZ: 0,
      upX: 0,
      upY: 0,
      upZ: 1,
      ortho: false,
      cameraFar: 20000,
    });
  };

  const layoutSelection = () => {
    if (!viewerSystem.current) {
      return;
    }
    const viewer = viewerSystem.current.viewer;
    const children = viewer.scene.scene.children;
    for (let i = 0, l = children.length; i < l; i += 1) {
      const mesh = children[i];
      if (!mesh.userData.isPropRoot) {
        continue;
      }
      mesh.visible = stringApplySearch(mesh.name, filterString);
    }
    viewer.scene.markShadowMapsForUpdate();
    viewer.scene.markRenderingNeedsUpdate();
  };

  async function loadPropsInBatches(modelUrls: string[], batchSize: number): Promise<THREE.Object3D[]> {
    const results: THREE.Object3D[] = [];

    for (let i = 0; i < modelUrls.length; i += batchSize) {
      const batch = modelUrls.slice(i, i + batchSize).map(async (url) => {
        assertIsNotNull(viewerSystem.current);
        return loadPropFromGLTF(assetLoader, url, viewerSystem.current);
      });

      // Wait for the current batch to complete before moving to the next
      const batchResults = await Promise.all(batch);
      results.push(...batchResults);
    }

    return results;
  }

  async function loadProps(names: string[], categoryName: string) {
    const namesToLoad = names.filter((name) => !loadedProps.current.hasOwnProperty(name));
    const modelUrls = namesToLoad.map((name) => propGltfFileNameFromName(name));
    const props = await loadPropsInBatches(modelUrls, 16);

    let totalDataStats: MeshDataStats = meshDataStatsNew();

    for (let i = 0; i < namesToLoad.length; i++) {
      const prop = props[i];
      prop.userData.isPropRoot = true;

      const displayedName = trimDisplayedName(namesToLoad[i]);

      prop.traverse((node) => {
        if (node instanceof Mesh) {
          // Set the prop name on the mesh objects so it can be displayed in the UI later.
          // The mesh name that's loaded from inside the GLTF file could be outdated if the prop has been renamed.
          node.name = displayedName;

          node.castShadow = true;
          node.receiveShadow = true;
          node.userData.isProp = true;

          const nodeDataStats = dataStatsFromThreeMesh(node);
          totalDataStats = meshDataStatsSum(totalDataStats, nodeDataStats);
        }
      });

      prop.setRotationFromEuler(new Euler());
      prop.updateMatrix();
      prop.updateMatrixWorld();

      loadedProps.current[namesToLoad[i]] = prop;
    }

    if (!categoryStatsMap.current.hasOwnProperty(categoryName)) {
      categoryStatsMap.current[categoryName] = {
        numberOfProps: namesToLoad.length,
        totalMemory: totalDataStats.memoryBytes,
        totalTriangles: totalDataStats.numTris,
        totalVertices: totalDataStats.numVerts,
      };
    }

    setCategoryStats(categoryStatsMap.current[categoryName]);
  }

  const loadPropCategories = async () => {
    const propLibrary = await loadPropLibrary();
    const names = [...propLibrary.values().map((propAsset) => propAsset.name)].sort();
    const loadedCategories = {};
    const categoryNames = names.map((name) => splitPropCategoryFromName(name));
    categoryNames.forEach((x, i) => {
      if (!loadedCategories[x]) {
        loadedCategories[x] = [];
      }
      loadedCategories[x].push(names[i]);
    });

    const firstSelectedCategory = Object.keys(loadedCategories)[0];

    await loadProps(loadedCategories[firstSelectedCategory], firstSelectedCategory);

    setCategories(loadedCategories);
    setState({
      ...state,
      selectedTriangles: 0,
      selectedVertices: 0,
      selectedMemory: 0,
      selectedSubmeshTriangles: 0,
      selectedSubmeshVertices: 0,
      selectedProp: "",
    });
    setSelectedCategory(firstSelectedCategory);
  };

  const baseBoxStyles = { position: "absolute" as const, zIndex: 1000 };

  const propsList: string[] = selectedCategory ? categories[selectedCategory] : [];

  const propsListElements = propsList
    .filter((propName) => stringApplySearch(propName, filterString))
    .map((propName) => (
      <li key={propName}>
        <Button btnStyle="link" btnSize="medium" onClick={() => selectMesh(loadedProps.current[propName])}>
          {trimDisplayedName(propName)}
        </Button>
      </li>
    ));

  return (
    <>
      <Head>
        <title>{getPageTitle("Props")}</title>
        <meta name="robots" content="noindex" />
        <style>{`
          body {
            overflow: hidden;
          }

          .FullScreenBox_wrapper {
            overflow: hidden;
          }

          .filterControlsColumn {
            height: 100%;
            min-width: 350px;
            display: flex;
            flex-direction: column;
            background-color: rgba(255, 255, 255, 0.5);
            backdrop-filter: blur(10px);
          }

          .controls {
            padding: 10px;
            resize: none;
            text-align: right;
          }

          .controls input {
            width: 100%;
          }

          .scrollableList {
            flex: 1;
            overflow-y: scroll;
          }

          li {
            text-align: right;
          }

          .statsContainer {
            position: absolute;
            z-index: 1000;
            left: 190px;
            right: 370px;
            bottom: 20px;
            display: flex;
            flex-direction: column;
            gap: 20px;
          }

          .statsBox {
            background-color: rgba(255, 255, 255, 0.5);
            backdrop-filter: blur(10px);
            padding: 10px;
            border-radius: 4px;
          }

          .statsRow {
            display: flex;
            flex-wrap: wrap;
            gap: 10px;
          }
        `}</style>
      </Head>

      <div style={{ position: "absolute", top: 0, left: 0, overflow: "hidden", width: "100vw", height: "100vh" }}>
        <div
          style={{
            ...baseBoxStyles,
            border: "1px solid black",
            left: 20,
            bottom: 20,
            width: 150,
            height: 150,
          }}
          ref={svgViewerContainer}
        />

        <div className="statsContainer">
          {state.selectedProp !== "" && (
            <div className="statsBox">
              <div className="statsRow">
                <span>Selected: {state.selectedProp}</span>
                <span>Selected Prop Memory: {formatBytes(state.selectedMemory)}</span>
                <span>Selected Prop Triangles: {state.selectedTriangles}</span>
                <span>Selected Prop Vertices: {state.selectedVertices}</span>
                <span>Selected Prop Bounding Box: {state.selectedBoundingBox}</span>
                <span>Selected Submesh Triangles: {state.selectedSubmeshTriangles}</span>
                <span>Selected Submesh Vertices: {state.selectedSubmeshVertices}</span>
              </div>
            </div>
          )}

          <div className="statsBox">
            <b>Debug visualization</b>
            <Checkbox
              checked={duplicateTrisDebugVisEnabled}
              label={
                <span>
                  <ColorPreview color={DUPLICATE_TRI_COLOR} /> Duplicate triangles
                </span>
              }
              onChange={(e) => setDuplicateTrisDebugVisEnabled(e.currentTarget.checked)}
            />
            <Checkbox
              checked={uvDebugVisEnabled}
              label={
                <span>
                  Edges with stretched UVs (<ColorPreview color={STRETCHED_UV_COLOR} />{" "}
                  <ColorPreview color={STRETCHED_UV_COLOR2} />: UVs should be scaled up,{" "}
                  <ColorPreview color={STRETCHED_UV_COLOR3} /> <ColorPreview color={STRETCHED_UV_COLOR4} />: UVs should
                  be scaled down)
                </span>
              }
              onChange={(e) => setUvDebugVisEnabled(e.currentTarget.checked)}
            />
            <Checkbox
              checked={holeDebugVisEnabled}
              label={
                <span>
                  <ColorPreview color={HOLE_COLOR} /> Holes in geometry and <ColorPreview color={NON_MANIFOLD_COLOR} />{" "}
                  non-manifold edges
                </span>
              }
              onChange={(e) => setHoleDebugVisEnabled(e.currentTarget.checked)}
            />
            <div style={{ marginTop: "10px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px", marginTop: "5px" }}>
                <label>UV scaling tolerance for debug visualization</label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={uvScalingTolerance}
                  onChange={(e) => setUvScalingTolerance(parseFloat(e.currentTarget.value))}
                  style={{ flex: 1 }}
                />
                <span>{Math.round(uvScalingTolerance * 100)}%</span>
              </div>
            </div>
            <div style={{ marginTop: "10px" }}>
              <Checkbox
                checked={uvVisualizationEnabled}
                label="UV visualization mode"
                onChange={(e) => setUvVisualizationEnabled(e.currentTarget.checked)}
              />
            </div>
          </div>

          <div className="statsBox">
            <div className="statsRow">
              <span>Props in Category: {categoryStats.numberOfProps}</span>
              <span>Category Total Memory: {formatBytes(categoryStats.totalMemory)}</span>
              <span>Category Total Triangles: {categoryStats.totalTriangles}</span>
              <span>Category Total Vertices: {categoryStats.totalVertices}</span>
            </div>
          </div>
        </div>

        <div style={{ ...baseBoxStyles, right: 0, top: 0 }} className="filterControlsColumn">
          <div className="controls">
            <PaddedLabel>Filter by category</PaddedLabel>
            <select
              onChange={(event) => {
                setState({ ...state, selectedProp: "" });
                setSelectedCategory(event.currentTarget.value);
                setFilterString("");
              }}
            >
              {Object.keys(categories).map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
            <div style={{ marginTop: 10 }}>
              <input
                type="text"
                placeholder="Filter list by name"
                onChange={(ev) => setFilterString(ev.target.value)}
                value={filterString}
              />
            </div>
          </div>
          <div className="scrollableList">
            <ul style={{ listStyleType: "none", textAlign: "right" }}>{propsListElements}</ul>
          </div>
        </div>

        <div
          style={{
            position: "absolute",
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            width: "100%",
            height: "100%",
            cursor: canvasCursor,
          }}
          onMouseDown={mouseDown}
          onMouseMove={mouseMove}
          onMouseUp={mouseUp}
          ref={viewerContainer}
        />
      </div>
    </>
  );
}

export const Route = createFileRoute("/debug/props")({
  component: withAuthentication(DebugPropsPage, { superAdmin: true }),
});
